import React, { useEffect, useMemo, useState } from "react";
import { FleetDefaults } from "@app/shared/src/models";
import { Card, CardHead, FRow, FVal, Select, Spinner, Tag, useToast } from "@app/shared/src/components_v2";
import { SaveBar } from "../shared/SaveBar";
import { Toggle } from "../shared/Toggle";

/**
 * How much a driver may change from the cab.
 *
 * Fleet-level only: there is no per-device storage for these, so a truck cannot diverge and the
 * device page shows the org's answer read-only rather than a control that would silently do nothing.
 *
 * Impending collision is deliberately absent. A driver can ask for an earlier warning, never a later
 * one and never none at all, so there is no permission to grant — the protection is the reason the
 * unit is on the truck.
 */

/** Every in-cab change is transient, so the longest a horn can be silenced is bounded. */
const SNOOZE_CHOICES = [5, 15, 30, 60];

type Row = {
    key: keyof FleetDefaults & string;
    title: string;
    sub: string;
};

const PERMISSION_ROWS: Row[] = [
    { key: "driverMayToggleSpeeding", title: "Toggle the speeding alert", sub: "On or off for the shift" },
    { key: "driverMayToggleLeftPass", title: "Toggle the left-side pass alert", sub: "Traffic side of the truck" },
    { key: "driverMayToggleRightPass", title: "Toggle the right-side pass alert", sub: "Shoulder side of the truck" },
    { key: "driverMaySnoozeHorn", title: "Snooze the horn", sub: "Lights keep firing while snoozed" },
    { key: "driverMayRequestClips", title: "Request clips", sub: "Short pulls from the unit's DVR" },
    {
        key: "driverMayChangeCollisionSettings",
        title: "Change collision settings",
        sub: "Minimum speed and reaction time — earlier warning only",
    },
];

type Draft = Record<string, boolean | number>;

const toDraft = (d: FleetDefaults): Draft => {
    const draft: Draft = { driverMaxSnoozeSeconds: d.driverMaxSnoozeSeconds };
    for (const row of PERMISSION_ROWS) draft[row.key] = Boolean(d[row.key]);
    return draft;
};

export const DriverAccessSection: React.FC<{
    defaults?: FleetDefaults;
    /** Supplied only where these are editable — the fleet defaults page. */
    onSave?: (apply: (target: FleetDefaults) => void) => Promise<void>;
}> = ({ defaults, onSave }) => {
    const toast = useToast();
    const [draft, setDraft] = useState<Draft>();
    const [busy, setBusy] = useState(false);

    const saved = useMemo(() => (defaults ? toDraft(defaults) : undefined), [defaults]);
    useEffect(() => setDraft(saved), [saved]);

    if (!draft || !saved || !defaults) {
        return (
            <Card>
                <div className="grid place-items-center p-12 text-[var(--muted)]">
                    <Spinner />
                </div>
            </Card>
        );
    }

    const editable = Boolean(onSave);
    const dirty = JSON.stringify(draft) !== JSON.stringify(saved);

    const commit = async () => {
        if (!onSave) return;
        setBusy(true);
        try {
            await onSave((target) => {
                for (const row of PERMISSION_ROWS) {
                    (target as unknown as Record<string, unknown>)[row.key] = draft[row.key];
                }
                target.driverMaxSnoozeSeconds = Number(draft.driverMaxSnoozeSeconds);
            });
            toast("Driver access saved");
        } catch (err) {
            toast(err instanceof Error ? err.message : "Could not save that");
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <Card>
                <CardHead title="What the driver can change" note="from the cab QR code" />
                <div className="mt-2">
                    {PERMISSION_ROWS.map((row) => (
                        <FRow key={row.key} title={row.title} sub={row.sub}>
                            {editable ? (
                                <Toggle
                                    on={Boolean(draft[row.key])}
                                    onChange={(v) => setDraft({ ...draft, [row.key]: v })}
                                    label={row.title}
                                />
                            ) : Boolean(draft[row.key]) ? (
                                <Tag tone="safe">Allowed</Tag>
                            ) : (
                                <Tag tone="mute">Not allowed</Tag>
                            )}
                        </FRow>
                    ))}

                    <FRow title="Maximum snooze" sub="The longest a driver may silence the horn">
                        {editable ? (
                            <Select
                                value={String(draft.driverMaxSnoozeSeconds)}
                                onChange={(e) => setDraft({ ...draft, driverMaxSnoozeSeconds: Number(e.target.value) })}
                                aria-label="Maximum snooze"
                                disabled={!draft.driverMaySnoozeHorn}
                            >
                                {SNOOZE_CHOICES.map((m) => (
                                    <option key={m} value={m * 60}>
                                        {m} min
                                    </option>
                                ))}
                            </Select>
                        ) : (
                            <FVal mu>{Math.round(Number(draft.driverMaxSnoozeSeconds) / 60)} min</FVal>
                        )}
                    </FRow>

                    <FRow title="Change anything persistently" sub="Every in-cab change reverts when the unit restarts">
                        <Tag tone="mute">Never</Tag>
                    </FRow>
                </div>
                <div className="border-t border-[var(--line)] bg-[var(--surface-2)] px-5 py-3 text-[12px] leading-relaxed text-[var(--muted)]">
                    {editable ? (
                        <>
                            Applies to every truck in the organization. Collision protection is not listed because it cannot be
                            switched off from the cab at all — a driver may only ask for an earlier warning.
                        </>
                    ) : (
                        <>
                            Set once for the whole organization under{" "}
                            <b className="font-semibold text-[var(--text)]">Settings → Driver access</b>. A truck cannot differ
                            from it.
                        </>
                    )}
                </div>
            </Card>

            <SaveBar
                show={editable && dirty}
                busy={busy}
                label="Unsaved · driver access"
                onSave={commit}
                onDiscard={() => setDraft(saved)}
            />
        </>
    );
};
